import React, { memo, useCallback } from 'react';
import { Card, Drawer, Empty, Form, Typography } from 'antd';
import { useDrop } from 'react-dnd';
import { useImmer } from 'use-immer';
import { Identifier } from 'dnd-core';
import { nanoid } from 'nanoid';
import { ToolboxFormItem } from '../Toolbar';
import FormItem from './FormItem';
import PreferencesForm from './PreferencesForm';
import styles from './styles.module.pcss';
import { If } from '@/components';

export type DraggableFormItem = ToolboxFormItem & {
  index: number;
  id: string;
  name: string;
  parameter?: Record<'key' | 'value', string>;
  placeholder?: string;
  required?: boolean;
  color: string;
  options?: Record<'key' | 'label' | 'value', string>[];
};

type State = {
  formItems: DraggableFormItem[];
  editDrawerOpen: boolean;
  editFormItemIndex?: number;
};

const WidgetForm = memo(() => {
  const [form] = Form.useForm();

  const [state, setState] = useImmer<State>({
    formItems: [],
    editDrawerOpen: false,
  });

  const [{ handlerId }, drop] = useDrop<ToolboxFormItem, void, { handlerId: Identifier | null }>(() => ({
    accept: 'TOOLBARITEM',
    drop: (item) => {
      setState((draft) => {
        const id = nanoid();
        const options = item.withOptions
          ? [
              { key: nanoid(), label: 'Option 1', value: 'Value 1' },
              { key: nanoid(), label: 'Option 2', value: 'Value 2' },
              { key: nanoid(), label: 'Option 3', value: 'Value 3' },
            ]
          : undefined;
        draft.formItems.push({
          id,
          index: draft.formItems.length,
          name: `Arya-${id}`,
          color: 'var(--arya-primary-color)',
          options,
          ...item,
        });
      });
    },
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
  }));

  const onChangeOrder = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setState((draft) => {
        const nextState = draft.formItems.slice();
        nextState.splice(dragIndex, 1);
        nextState.splice(hoverIndex, 0, draft.formItems[dragIndex]);
        draft.formItems = nextState;
      });
    },
    [setState]
  );

  const onDeleteFormItem = useCallback(
    (id: string) => {
      setState((draft) => {
        const index = draft.formItems.findIndex((item) => item.id === id);
        if (index !== -1) {
          draft.formItems.splice(index, 1);
        }
      });
    },
    [setState]
  );

  const toggleEditDrawer = useCallback(
    (open: boolean, id?: string) => {
      setState((draft) => {
        const editFormItemIndex = draft.formItems.findIndex((item) => item.id === id);
        draft.editDrawerOpen = open;
        draft.editFormItemIndex = editFormItemIndex;
      });
    },
    [setState]
  );

  const renderDragFormItem = useCallback(
    (item: DraggableFormItem, index: number) => (
      <FormItem
        key={item.id}
        formItem={item}
        onChangeOrder={onChangeOrder}
        index={index}
        onDeleteFormItem={onDeleteFormItem}
        toggleEditDrawer={toggleEditDrawer}
      />
    ),
    [onChangeOrder, onDeleteFormItem, toggleEditDrawer]
  );

  return (
    <Form form={form} className={styles.root}>
      <Card ref={drop} data-testid={handlerId} className={styles.card}>
        <If condition={!state.formItems.length}>
          <Empty
            description={
              <Typography.Title type="secondary" level={2}>
                Dropzone
              </Typography.Title>
            }
            image={null}
          />
        </If>
        <Card type="inner" variant="borderless">
          {state.formItems.map((item, index) => renderDragFormItem(item, index))}
        </Card>
      </Card>
      {state.editDrawerOpen && state.editFormItemIndex !== undefined ? (
        <Drawer
          title={state.formItems[state.editFormItemIndex].label}
          placement="left"
          closable={false}
          onClose={() => toggleEditDrawer(false)}
          open={state.editDrawerOpen}
          width="50%"
        >
          <PreferencesForm formItem={state.formItems[state.editFormItemIndex]} />
        </Drawer>
      ) : null}
    </Form>
  );
});

export default WidgetForm;
